import { log } from 'evlog/client';
import { CalendarSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData, useNavigate } from 'react-router';
import { toast } from 'sonner';

import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Label,
} from '@virtual-care/shadcn';
import {
  apiErrorToMessage,
  resolveErrorMessage,
  useAction,
} from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import {
  DetailPill,
  PaymentDetailsCard,
} from '../../components/booking/payment-card';
import { PromoCodeInput } from '../../components/booking/promo-code-input.tsx';
import { ProfileCard } from '../../components/header';
import Header from '../../components/header/header';
import { useReservation } from '../../hooks/reservation/context.tsx';
import {
  type SecondOpinionReservation,
  isSecondOpinionReady,
  secondOpinionReservationStore,
} from '../../hooks/reservation/second-opinion.ts';
import { useBookingDetails } from '../../hooks/use-booking-details.ts';
import { toSar, usePaymentInfo } from '../../hooks/use-payment-info.ts';
import { useSettings } from '../../hooks/use-settings.tsx';

type ConfirmationActionsProps = {
  onClick: () => void;
  label: string;
  isLoading?: boolean;
};

const ConfirmationActions = ({
  onClick,
  label,
  isLoading,
}: ConfirmationActionsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  return (
    <CardFooter className="flex w-full justify-center gap-4">
      <Button
        type="button"
        variant={'outline'}
        onClick={() => navigate(-1)}
        className="secondary-button"
      >
        {t('secondOpinion.confirmation.back')}
      </Button>
      <Button
        variant={'default'}
        onClick={onClick}
        disabled={isLoading}
        className="primary-button"
      >
        {isLoading ? t('secondOpinion.confirmation.processing') : label}
      </Button>
    </CardFooter>
  );
};

export default function SecondOpinionBookingConfirmation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const data = useLoaderData() as Awaited<
    ReturnType<typeof secondOpinionBookingConfirmationLoader>
  >;

  const isB2B = data.identity.userType === 'B2B';

  const hasOnBehalfInfo = !!data.reservation.patientName;

  const bookingDetails = useBookingDetails(
    data.identity,
    t('common.services.secondOpinion'),
    hasOnBehalfInfo
      ? {
          patientName: data.reservation.patientName,
          patientBirthDate: data.reservation.patientBirthDate,
          patientGender: data.reservation.patientGender,
        }
      : undefined,
  );

  const [reservation, setReservation] =
    useReservation<SecondOpinionReservation>();
  const payment = usePaymentInfo({
    kind: 'second_opinion',
    urgent: data.reservation.urgent,
    appliedPromo: reservation?.appliedPromo,
  });
  const createReservation = useAction('POST /second-opinion/b2b', {
    invalidate: ['GET /patients/me/second-opinions'],
  });

  const settings = useSettings();
  const urgentFee = toSar(
    settings
      ? Number.parseInt(settings.secondOpinionUrgentFee.value) -
          Number.parseInt(settings.secondOpinionFee.value, 10)
      : 0,
  );

  const handleConfirm = async () => {
    log.info({ action: 'second_opinion_confirm', isB2B });
    if (!isB2B) {
      return navigate('/second-opinion/payments');
    }

    const {
      specialityId,
      description,
      files,
      urgent,
      doctorName,
      patientName,
      patientBirthDate,
      patientGender,
    } = data.reservation;

    if (!doctorName) {
      toast.error(t('secondOpinion.confirmation.missingDoctorName'));
      return;
    }

    try {
      await createReservation.mutateAsync({
        specialityId,
        urgent,
        description,
        doctorName,
        ...(files ? { files } : {}),
        ...(patientName ? { patientName } : {}),
        ...(patientBirthDate ? { patientBirthDate } : {}),
        ...(patientGender === 'male' || patientGender === 'female'
          ? { patientGender }
          : {}),
      });

      navigate('/second-opinion/request-status?ok=1');
    } catch (error) {
      log.error({ action: 'second_opinion_confirm', error });
      toast.error(resolveErrorMessage(apiErrorToMessage(error)));
    }
  };

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('common.services.secondOpinion')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('secondOpinion.confirmation.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('secondOpinion.confirmation.subtitle')}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="rounded-xl border bg-white p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">
                {t('secondOpinion.confirmation.detailsHeader')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(bookingDetails).map(([key, value]) => (
                  <DetailPill key={key} label={key} value={value} />
                ))}
              </div>
              <div className="mt-6 flex items-center gap-x-1">
                <Checkbox
                  checked={data.reservation.urgent}
                  id="urgent"
                  disabled
                />
                <Label htmlFor="urgent" className="sr-only font-bold">
                  {data.reservation.urgent
                    ? t('secondOpinion.confirmation.urgentLabel')
                    : t('secondOpinion.confirmation.notUrgentLabel')}
                </Label>
                <p className="text-sm font-bold">
                  {isB2B
                    ? data.reservation.urgent
                      ? t('secondOpinion.confirmation.urgentLabel')
                      : t('secondOpinion.confirmation.notUrgentLabel')
                    : data.reservation.urgent
                      ? t('secondOpinion.confirmation.urgentWithFee', {
                          fee: urgentFee,
                        })
                      : t('secondOpinion.confirmation.notUrgentDescription')}
                </p>
              </div>
            </div>
            {!isB2B && (
              <PromoCodeInput
                service="second_opinion"
                urgent={data.reservation.urgent}
                applied={reservation?.appliedPromo}
                onApply={(applied) =>
                  setReservation((current) =>
                    current ? { ...current, appliedPromo: applied } : current,
                  )
                }
              />
            )}
            {!isB2B && <PaymentDetailsCard payment={payment} />}
          </CardContent>
          <ConfirmationActions
            onClick={handleConfirm}
            label={
              isB2B
                ? t('secondOpinion.confirmation.confirm')
                : t('secondOpinion.confirmation.payAndConfirm')
            }
            isLoading={isB2B && createReservation.isPending}
          />
        </Card>
      </div>
    </>
  );
}

export async function secondOpinionBookingConfirmationLoader() {
  const reservation = secondOpinionReservationStore.read();
  const identity = await requiresAuth();
  if (!reservation || !isSecondOpinionReady(reservation)) {
    throw redirect('/second-opinion');
  }
  return { reservation, identity };
}
